import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BellSimpleRinging, BellSimple, CheckCircle, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { subscribeNews, unsubscribeNews, getSubscription } from "../lib/api";

const LS_EMAIL = "neet_news_email";
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Reusable email-subscribe control for a news scope ("central" | "colleges").
// Shows "Notify me"; once subscribed, shows a bold "Subscribed" state.
export function SubscribeButton({ scope, variant = "light", className = "", newsState = null, label = null, accent = null, subscribedLabel = null, bellSize = 15, bellClassName = "" }) {
  const [subscribed, setSubscribed] = useState(false);
  const [pending, setPending] = useState(false);
  const [subState, setSubState] = useState(null);   // colleges: the state actually subscribed to
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const perState = scope === "colleges";

  useEffect(() => {
    const saved = localStorage.getItem(LS_EMAIL) || "";
    setEmail(saved);
    if (saved && EMAIL_RX.test(saved) && (!perState || newsState)) {
      getSubscription(saved, scope, perState ? newsState : undefined).then((r) => {
        setSubscribed(!!r.data.subscribed); setPending(!!r.data.pending); setSubState(r.data.state || null);
      }).catch(() => {});
    } else {
      setSubscribed(false); setPending(false); setSubState(null);
    }
  }, [scope, newsState]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const dark = variant === "dark";
  const accentBg = accent === "orange"
    ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/25"
    : "bg-[#6043EF] text-white hover:bg-[#4c34c9] shadow-md shadow-[#6043EF]/20";
  const submitBg = accent === "orange" ? "bg-orange-500 hover:bg-orange-600" : "bg-[#6043EF] hover:bg-[#4c34c9]";
  const isSched = (scope || "").startsWith("sched_");
  const boardName = isSched
    ? (subscribedLabel ? subscribedLabel.replace(/^Subscribed\s·\s/, "") : (label ? label.replace(/^Subscribe for updates on\s/i, "") : "this board"))
    : null;
  const scopeLabel = isSched ? boardName : (scope === "central" ? "Central Newsroom" : (newsState ? `${newsState} colleges` : "Colleges News"));

  const doSubscribe = async () => {
    const em = email.trim().toLowerCase();
    if (!EMAIL_RX.test(em)) { toast.error("Enter a valid email"); return; }
    if (perState && !newsState) { toast.error("Pick a state first"); return; }
    setBusy(true);
    try {
      const r = await subscribeNews(em, scope, perState ? newsState : undefined);
      localStorage.setItem(LS_EMAIL, em);
      setOpen(false);
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
      if (r.data.subscribed) {
        setSubscribed(true); setPending(false); setSubState(perState ? newsState : null);
        toast.success(
          isSched ? `Subscribed to ${boardName} live status` : `Subscribed to ${scopeLabel} alerts`,
          { description: isSched
              ? `We'll email ${em} as this board progresses — new rounds, application & counselling windows opening, results.`
              : `We'll email ${em} as new notices are published.` });
      } else {
        setPending(true);
        toast.success("Verification email sent ✉️", {
          description: `Check ${em} and tap the link to confirm your ${isSched ? boardName + " live updates" : scopeLabel + " alerts"}.` });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not subscribe");
    } finally { setBusy(false); }
  };

  const doUnsubscribe = async () => {
    setBusy(true);
    try {
      await unsubscribeNews(email.trim().toLowerCase(), scope, perState ? (subState || newsState) : undefined);
      setSubscribed(false); setPending(false); setSubState(null); setOpen(false);
      toast(`Unsubscribed from ${scope === "central" ? "Central Newsroom" : `${subState || newsState} colleges`}`);
    } catch (e) { toast.error("Could not unsubscribe"); }
    finally { setBusy(false); }
  };

  const inputGroup = (onSubmit) => (
    <div className={`inline-flex items-center gap-1.5 ${className}`} ref={ref} data-testid={`subscribe-inline-${scope}`}>
      <input type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSubmit()} placeholder="you@email.com"
        data-testid={`subscribe-email-${scope}`}
        className="h-9 w-44 sm:w-56 px-3.5 rounded-full border border-zinc-200 bg-white text-[13px] text-[#09090B] outline-none focus:border-[#6043EF] shadow-sm" />
      <button onClick={onSubmit} disabled={busy} data-testid={`subscribe-submit-${scope}`}
        className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full ${submitBg} text-white disabled:opacity-50 transition-colors shadow-md`}>
        <PaperPlaneTilt size={15} weight="fill" />
      </button>
      <button onClick={() => setOpen(false)} data-testid={`subscribe-close-${scope}`}
        className={`shrink-0 inline-flex items-center justify-center w-7 h-9 rounded-full ${dark ? "text-white/70 hover:text-white" : "text-zinc-400 hover:text-zinc-700"}`}>
        <X size={15} weight="bold" />
      </button>
    </div>
  );

  // pending verification
  if (pending && !subscribed) {
    if (open) return inputGroup(doSubscribe); // resend to a (possibly new) email inline
    return (
      <button onClick={() => setOpen(true)} data-testid={`subscribe-pending-${scope}`}
        className={`group inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${className} ${
          dark ? "bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/40" : "bg-amber-50 text-amber-700 ring-1 ring-amber-300"}`}>
        <BellSimple size={15} weight="fill" /> Check inbox
      </button>
    );
  }

  // subscribed
  if (subscribed) {
    if (open) return (
      <div className={`inline-flex items-center gap-1.5 ${className}`} ref={ref} data-testid={`subscribe-inline-${scope}`}>
        <span className={`text-[11px] font-semibold ${dark ? "text-white/80" : "text-zinc-500"} whitespace-nowrap max-w-[160px] truncate`}>{email}</span>
        <button onClick={doUnsubscribe} disabled={busy} data-testid={`unsubscribe-btn-${scope}`}
          className="h-9 px-3.5 rounded-full bg-rose-50 text-rose-600 text-xs font-bold hover:bg-rose-100 disabled:opacity-50 transition-colors">
          {busy ? "…" : "Unsubscribe"}
        </button>
        <button onClick={() => setOpen(false)} className={`shrink-0 inline-flex items-center justify-center w-7 h-9 rounded-full ${dark ? "text-white/70 hover:text-white" : "text-zinc-400 hover:text-zinc-700"}`}><X size={15} weight="bold" /></button>
      </div>
    );
    const subText = subscribedLabel
      || (perState && subState ? `Subscribed · ${subState} news`
        : scope === "central" ? "Subscribed · Central news"
        : scope === "colleges" ? "Subscribed · Colleges news"
        : (scope || "").startsWith("sched_") ? "Subscribed to this board"
        : "Subscribed");
    return (
      <button onClick={() => setOpen(true)} data-testid={`subscribe-status-${scope}`}
        title={subText}
        className={`group inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-black uppercase tracking-wider transition-all ${className} ${
          dark ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-300"}`}>
        <CheckCircle size={16} weight="fill" /> {subText}
      </button>
    );
  }

  // idle
  if (open) return inputGroup(doSubscribe);
  return (
    <button onClick={() => setOpen(true)} data-testid={`subscribe-btn-${scope}`}
      title={perState && newsState ? `Get ${newsState} college alerts` : undefined}
      className={`group inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${className} ${
        dark ? "bg-white/10 text-white hover:bg-white/20 ring-1 ring-white/15" : accentBg}`}>
      <BellSimpleRinging size={bellSize} weight="fill" className={`group-hover:animate-[wiggle_.4s_ease] ${bellClassName}`} />
      {label || (perState && newsState ? `Notify · ${newsState}` : "Notify me")}
    </button>
  );
}
